import { useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useSidebar } from "../context/SidebarContext";
import { BrandMark } from "./BrandMark";
import { Footer } from "./Footer";

export function Layout({ children }: { children: ReactNode }) {
  const { username, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { open, setOpen, hasSidebar } = useSidebar();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="flex h-full flex-col">
      <header className="glass glass-strong z-30 flex h-14 flex-shrink-0 items-center gap-2 rounded-none border-x-0 border-t-0 px-3 sm:px-4">
        {hasSidebar && (
          <button
            onClick={() => setOpen(!open)}
            className="-ml-1 grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg text-zinc-600 transition-colors hover:bg-white/70 hover:text-zinc-900 lg:hidden"
            aria-label={open ? "Close folders" : "Open folders"}
            aria-expanded={open}
          >
            <svg className="h-[18px] w-[18px]" viewBox="0 0 20 20" fill="currentColor">
              {open ? (
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              ) : (
                <path d="M3 5.25A.75.75 0 0 1 3.75 4.5h12.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 5.25Zm0 4.75a.75.75 0 0 1 .75-.75h12.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 10Zm0 4.75a.75.75 0 0 1 .75-.75h12.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Z" />
              )}
            </svg>
          </button>
        )}

        <Link to="/app" className="flex min-w-0 items-center gap-2.5">
          <BrandMark />
          <span className="truncate text-[15px] font-semibold tracking-tight text-zinc-900">Images</span>
        </Link>

        <div className="relative ml-auto">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-1.5 text-sm text-zinc-700 transition-colors hover:bg-white/70 sm:pr-2"
          >
            <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg bg-zinc-900 text-2xs font-semibold text-white">
              {(username ?? "A").slice(0, 1).toUpperCase()}
            </span>
            <span className="hidden max-w-[10rem] truncate font-medium sm:inline">{username ?? "admin"}</span>
            <svg className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400" viewBox="0 0 20 20" fill="currentColor">
              <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.08 1.04l-4.25 4.25a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06Z" />
            </svg>
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="glass glass-strong absolute right-0 z-20 mt-2 w-48 overflow-hidden rounded-xl py-1 shadow-glass-lg animate-scale-in">
                <div className="truncate px-3 py-1.5 text-2xs text-zinc-500">Signed in as {username ?? "admin"}</div>
                <div className="my-1 border-t border-white/70" />
                <Link
                  to="/settings"
                  onClick={() => setMenuOpen(false)}
                  className="block px-3 py-2 text-[13px] text-zinc-700 transition-colors hover:bg-white/70"
                >
                  Settings
                </Link>
                <button
                  onClick={async () => {
                    setMenuOpen(false);
                    await logout();
                    toast.info("Signed out.");
                    navigate("/login");
                  }}
                  className="block w-full px-3 py-2 text-left text-[13px] text-zinc-700 transition-colors hover:bg-white/70"
                >
                  Log out
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      <main className="min-h-0 flex-1">{children}</main>

      <div className="glass glass-quiet flex-shrink-0 rounded-none border-x-0 border-b-0">
        <Footer inset />
      </div>
    </div>
  );
}
